import { memo } from 'react'
import type { CSSProperties } from 'react'
import type { LabelElement } from './types'
import { cssFontFamily } from './types'

type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

interface ResizePatch {
  x: number
  y: number
  width: number
  height: number
}

interface Props {
  element: LabelElement
  scale: number
  selected: boolean
  onSelect: (id: string) => void
  onMove: (id: string, x: number, y: number) => void
  onResize: (id: string, patch: ResizePatch) => void
  templateWidth: number
  templateHeight: number
}

const MIN_SIZE = 2

const round1 = (v: number) => Math.round(v * 10) / 10

const degToRad = (deg: number) => (deg * Math.PI) / 180

/** Half-size of axis-aligned bbox for w×h rect rotated around its center. */
const rotatedHalfExtents = (w: number, h: number, rotationDeg: number) => {
  const rad = degToRad(rotationDeg)
  const cos = Math.abs(Math.cos(rad))
  const sin = Math.abs(Math.sin(rad))
  return {
    halfW: (w * cos + h * sin) / 2,
    halfH: (w * sin + h * cos) / 2,
  }
}

/** Keep rotated element fully inside the label (x,y = unrotated top-left). */
const clampElementPosition = (
  x: number,
  y: number,
  w: number,
  h: number,
  rotationDeg: number,
  templateW: number,
  templateH: number,
): { x: number; y: number } => {
  const { halfW, halfH } = rotatedHalfExtents(w, h, rotationDeg)
  let cx = x + w / 2
  let cy = y + h / 2
  const minCx = halfW
  const maxCx = templateW - halfW
  const minCy = halfH
  const maxCy = templateH - halfH
  if (minCx > maxCx) cx = templateW / 2
  else cx = Math.max(minCx, Math.min(maxCx, cx))
  if (minCy > maxCy) cy = templateH / 2
  else cy = Math.max(minCy, Math.min(maxCy, cy))
  return { x: round1(cx - w / 2), y: round1(cy - h / 2) }
}

/** Map screen drag delta to element-local axes (resize handles). */
const screenDeltaToLocal = (dx: number, dy: number, rotationDeg: number) => {
  const rad = (-rotationDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return {
    dx: dx * cos - dy * sin,
    dy: dx * sin + dy * cos,
  }
}

const computeResize = (
  handle: HandleId,
  init: ResizePatch,
  localDx: number,
  localDy: number,
  templateWidth: number,
  templateHeight: number,
  lockHeight: boolean,
  rotationDeg: number,
): ResizePatch => {
  let { x, y, width, height } = init

  const clampSize = () => {
    width = Math.max(MIN_SIZE, width)
    height = Math.max(lockHeight ? init.height : MIN_SIZE, height)
  }

  switch (handle) {
    case 'e':
      width = init.width + localDx
      break
    case 'w':
      width = init.width - localDx
      x = init.x + localDx
      break
    case 's':
      if (!lockHeight) height = init.height + localDy
      break
    case 'n':
      if (!lockHeight) {
        height = init.height - localDy
        y = init.y + localDy
      }
      break
    case 'se':
      width = init.width + localDx
      if (!lockHeight) height = init.height + localDy
      break
    case 'sw':
      width = init.width - localDx
      x = init.x + localDx
      if (!lockHeight) height = init.height + localDy
      break
    case 'ne':
      width = init.width + localDx
      if (!lockHeight) {
        height = init.height - localDy
        y = init.y + localDy
      }
      break
    case 'nw':
      width = init.width - localDx
      x = init.x + localDx
      if (!lockHeight) {
        height = init.height - localDy
        y = init.y + localDy
      }
      break
  }

  clampSize()
  const clamped = clampElementPosition(x, y, width, height, rotationDeg, templateWidth, templateHeight)
  return { x: clamped.x, y: clamped.y, width: round1(width), height: round1(height) }
}

const HANDLES: { id: HandleId; cursor: string; className: string }[] = [
  { id: 'nw', cursor: 'nwse-resize', className: 'top-0 left-0 -translate-x-1/2 -translate-y-1/2' },
  { id: 'n', cursor: 'ns-resize', className: 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2' },
  { id: 'ne', cursor: 'nesw-resize', className: 'top-0 right-0 translate-x-1/2 -translate-y-1/2' },
  { id: 'e', cursor: 'ew-resize', className: 'top-1/2 right-0 translate-x-1/2 -translate-y-1/2' },
  { id: 'se', cursor: 'nwse-resize', className: 'bottom-0 right-0 translate-x-1/2 translate-y-1/2' },
  { id: 's', cursor: 'ns-resize', className: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2' },
  { id: 'sw', cursor: 'nesw-resize', className: 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2' },
  { id: 'w', cursor: 'ew-resize', className: 'top-1/2 left-0 -translate-x-1/2 -translate-y-1/2' },
]

export const CanvasElement = memo(function CanvasElement({
  element: elem,
  scale,
  selected,
  onSelect,
  onMove,
  onResize,
  templateWidth,
  templateHeight,
}: Props) {
  const rotation = elem.rotation || 0
  const lockHeight = elem.type === 'line'

  const startMove = (e: React.MouseEvent) => {
    if (e.button !== 0) return
    e.stopPropagation()

    const startX = e.clientX
    const startY = e.clientY
    const initX = elem.x
    const initY = elem.y

    const initW = elem.width
    const initH = elem.height

    const handleMove = (me: MouseEvent) => {
      const dx = (me.clientX - startX) / scale
      const dy = (me.clientY - startY) / scale
      const { x: nx, y: ny } = clampElementPosition(
        initX + dx,
        initY + dy,
        initW,
        initH,
        rotation,
        templateWidth,
        templateHeight,
      )
      onMove(elem.id, nx, ny)
    }

    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
    }

    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
  }

  const startResize = (e: React.MouseEvent, handle: HandleId) => {
    if (e.button !== 0) return
    e.stopPropagation()

    if (lockHeight && (handle === 'n' || handle === 's' || handle === 'nw' || handle === 'ne' || handle === 'sw' || handle === 'se')) {
      return
    }

    const startX = e.clientX
    const startY = e.clientY
    const init: ResizePatch = { x: elem.x, y: elem.y, width: elem.width, height: elem.height }

    const handleMove = (me: MouseEvent) => {
      const dx = (me.clientX - startX) / scale
      const dy = (me.clientY - startY) / scale
      const { dx: ldx, dy: ldy } = screenDeltaToLocal(dx, dy, rotation)
      const patch = computeResize(handle, init, ldx, ldy, templateWidth, templateHeight, lockHeight, rotation)
      onResize(elem.id, patch)
    }

    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
    }

    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
  }

  const style: CSSProperties = {
    position: 'absolute',
    left: elem.x * scale,
    top: elem.y * scale,
    width: elem.width * scale,
    height: elem.height * scale,
    transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
    transformOrigin: 'center center',
    userSelect: 'none',
    border: selected ? '2px solid #3b82f6' : '2px solid transparent',
    background: selected ? 'rgba(59,130,246,0.1)' : undefined,
  }

  let content: React.ReactNode = null
  const extraClass: string[] = []

  switch (elem.type) {
    case 'text': {
      const displayText =
        elem.label ||
        (elem.field ? `{${elem.field}}` : 'Текст')
      const wrapMode = elem.wrapMode || 'wrap_words'
      const overflowX = (elem.overflowX || 'visible') as CSSProperties['overflowX']
      const overflowY = (elem.overflowY || 'visible') as CSSProperties['overflowY']
      content = (
        <span
          style={{
            fontSize: (elem.fontSize || 10) * scale,
            fontWeight: elem.fontWeight || 'normal',
            textAlign: (elem.align as CSSProperties['textAlign']) || 'left',
            display: 'block',
            width: '100%',
            height: '100%',
            lineHeight: 1,
            fontFamily: cssFontFamily(elem.fontFamily),
            overflowX,
            overflowY,
            whiteSpace: wrapMode === 'no_wrap' ? 'nowrap' : 'normal',
            wordBreak: wrapMode === 'break_all' ? 'break-all' : 'normal',
            overflowWrap: wrapMode === 'wrap_words' ? 'break-word' : 'normal',
          }}
        >
          {displayText}
        </span>
      )
      break
    }
    case 'datamatrix':
      extraClass.push('bg-[repeating-linear-gradient(45deg,#d1d5db_0px,#d1d5db_2px,white_2px,white_4px)]')
      content = <span className="text-xs text-gray-500 pointer-events-none">GS1-DataMatrix</span>
      break
    case 'datamatrix_ka':
      extraClass.push('bg-[repeating-linear-gradient(45deg,#d1d5db_0px,#d1d5db_2px,white_2px,white_4px)]')
      content = <span className="text-xs text-gray-500 pointer-events-none">GS1-DataMatrix (КА)</span>
      break
    case 'gs1_128':
      extraClass.push('bg-[repeating-linear-gradient(45deg,#d1d5db_0px,#d1d5db_2px,white_2px,white_4px)]')
      content = <span className="text-xs text-gray-500 pointer-events-none">GS1-128</span>
      break
    case 'ean13':
      content = <span className="text-xs text-gray-500 pointer-events-none">EAN-13</span>
      extraClass.push('bg-[repeating-linear-gradient(90deg,#000_0px,#000_2px,transparent_2px,transparent_4px)]')
      break
    case 'line':
      style.height = 2
      style.background = '#374151'
      break
    case 'rect':
      style.border = selected ? '2px solid #3b82f6' : '2px solid #374151'
      style.background = 'transparent'
      break
  }

  const visibleHandles = lockHeight
    ? HANDLES.filter(h => h.id === 'e' || h.id === 'w')
    : HANDLES

  const contentAlign =
    elem.type === 'text'
      ? `items-start ${
          elem.align === 'center' ? 'justify-center' : elem.align === 'right' ? 'justify-end' : 'justify-start'
        }`
      : 'items-center justify-center'

  return (
    <div
      style={style}
      className={`${extraClass.join(' ')}`}
      onClick={(e) => { e.stopPropagation(); onSelect(elem.id) }}
    >
      <div
        className={`absolute inset-0 flex ${contentAlign} cursor-move hover:border-blue-300 ${selected ? '' : 'border-2 border-transparent'}`}
        onMouseDown={startMove}
      >
        {content}
      </div>

      {selected && visibleHandles.map(h => {
        const isCorner = h.id.length === 2
        return (
          <div
            key={h.id}
            role="presentation"
            className={`absolute z-10 bg-white border-2 border-blue-500 ${h.className} ${
              isCorner ? 'w-2 h-2' : h.id === 'n' || h.id === 's' ? 'w-4 h-1.5' : 'w-1.5 h-4'
            }`}
            style={{ cursor: h.cursor }}
            onMouseDown={e => startResize(e, h.id)}
          />
        )
      })}

      {rotation !== 0 && (
        <span
          className="absolute -top-5 -right-5 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded pointer-events-none"
        >
          {rotation}°
        </span>
      )}
    </div>
  )
})
